import { TraceColorPicker } from "./TraceColorPicker"
import { ModalityConfigurationDiv } from "../../../../../Constants/StyledComponents"
import React, { useContext, useEffect, useRef, useState } from "react"
import {
	MobergRow,
	MobergDropdown,
	MobergButton,
	MobergButtonShape,
	MobergIconSize,
	MobergTheme,
	MobergButtonVariant,
	MobergInputLabel,
	MobergShortTextInput,
	MobergColumn,
	MobergCheckbox,
} from "../../../../../Moberg"
import { LineTraceConfig, LineTraceConfigJSON, RenderStrategy, TraceConfigJSON, TraceOption } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { MdClose, MdSettings } from "react-icons/md"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { useModalProvider } from "../../../../../Providers/ModalProvider"
import { EditAnalysisModal } from "../../../../../Pages/Data/Visualize/DataReview/Components/Modals/EditAnalysis/EditAnalysisModal"
import { getAnalyticDisplayName, OnDemandAnalysis } from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"
import { LineGraphModalitiesContext } from "./LineGraphModalities"
import { useEnvironmentVariablesProvider } from "../../../../../Providers/EnvironmentVariablesProvider"
import { SourceDataOriginSummary } from "../SourceDataOriginSummary"
import { useRecoilValue } from "recoil"
import { currentPatientFileInfoAtom } from "../../../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile"

export const SourceDataLineModalities: React.FC = () => {
	const { sourceTraceOptions, currentGraph, reasonAddMeasurementIsDisabled, updateGraphProperty, removeTrace } = useContext(ConfigureWindowModalContext)
	const { activeSourceTraces, updateTrace, handleColorChange } = useContext(LineGraphModalitiesContext)
	const { environmentVariables } = useEnvironmentVariablesProvider()
	const { sources } = useRecoilValue(currentPatientFileInfoAtom)
	const [colorPickerTraceId, setColorPickerTraceId] = useState<string>()
	const colorPickerBlockRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())
	const colorPickerRef = useRef<HTMLDivElement | null>(null)
	const lineTraceOptions = sourceTraceOptions.filter((option: TraceOption) => option.supportedRenderStrategies.includes(RenderStrategy.LINE))

	const { createModal } = useModalProvider()

	function addModality() {
		const newTrace: LineTraceConfigJSON = {
			...lineTraceOptions[0].value,
			id: `trace-${new Date(Date.now()).toISOString()}`,
			renderStrategy: RenderStrategy.LINE,
			color: "#000",
		}

		updateGraphProperty("traces", (previous: TraceConfigJSON[]) => [...previous, newTrace])
		updateTrace(newTrace.id, newTrace, { useModalitySpecificDefaults: true })
	}

	function toggleColorPicker(event: any, traceId: string) {
		const node = colorPickerRef.current

		if (node?.style.display === "block" && traceId === colorPickerTraceId) {
			node.style.display = "none"
			return
		}

		setColorPickerTraceId(traceId)

		if (node) {
			node.style.display = "block"
			node.style.top = `${event.target.getBoundingClientRect().bottom}px`
			node.style.left = `${event.target.getBoundingClientRect().right}px`
		}
	}

	function addColorPickerBlockRef(node: HTMLDivElement | null, traceId: string) {
		colorPickerBlockRefs.current.set(traceId, node)
	}

	function traceAnalysisArgumentsChanged(trace: LineTraceConfig, newArgs: OnDemandAnalysis | undefined) {
		updateTrace(trace.id, {
			...trace,
			onDemandAnalysis: newArgs,
			dataKey: newArgs
				? `${trace.rawDataKey}-${JSON.stringify(newArgs)}` // we have to be able to uniquely identify analytic traces by the same name, but with different calculations.
				: trace.rawDataKey,
		})
	}

	useEffect(() => {
		const clickHandler = (event: any) => {
			let clickedOutside = true

			colorPickerBlockRefs.current.forEach(node => {
				if (node?.contains(event.target)) {
					clickedOutside = false
				}
			})

			if (colorPickerRef.current?.contains(event.target)) {
				clickedOutside = false
			}

			if (clickedOutside && colorPickerRef.current) {
				colorPickerRef.current.style.display = "none"
			}
		}

		document.addEventListener("click", clickHandler)
		return () => document.removeEventListener("click", clickHandler)
	})

	return (
		<ModalityConfigurationDiv>
			<MobergColumn gap="16px">
				<SourceDataOriginSummary />
				<div style={{ display: "grid", gridTemplateColumns: "auto auto auto auto auto 1fr", columnGap: "16px", rowGap: "12px", alignItems: "center" }}>
					<MobergInputLabel text={"Source"} />
					<MobergInputLabel text={"Label"} />
					<MobergInputLabel text={"Color"} />
					<MobergInputLabel text={"Post processing"} style={{ whiteSpace: "nowrap" }} />
					<MobergInputLabel text={"Visible"} />
					<MobergInputLabel text={""} />

					{activeSourceTraces.map(trace => {
						const sourceIsReadOnly = sources.get(trace.dataSource)?.read_only ?? true
						return (
							<React.Fragment key={trace.id}>
								<MobergDropdown
									options={lineTraceOptions}
									onChange={(traceConfig: LineTraceConfigJSON) => updateTrace(trace.id, traceConfig, { useModalitySpecificDefaults: true })}
									selectedValue={trace}
									equals={(a: LineTraceConfigJSON, b: LineTraceConfigJSON) =>
										a.rawDataKey === b.rawDataKey &&
										a.dataSource === b.dataSource &&
										(a.isCompositePart ? b.isCompositePart && a.compositeIndex === b.compositeIndex : !b.isCompositePart)
									}
									width={224}
								/>

								<MobergShortTextInput
									limit={20}
									defaultValue={trace.name}
									value={trace.name}
									onChange={(name: string) => updateTrace(trace.id, { ...trace, name })}
									style={{ width: "150px" }}
								/>

								<MobergRow expand={false}>
									<div
										onClick={event => toggleColorPicker(event, trace.id)}
										style={{
											width: "25px",
											height: "25px",
											backgroundColor: (trace as LineTraceConfigJSON).color ?? "#000",
											border: "1px solid var(--color-border-default)",
											borderRadius: "25px",
											cursor: "pointer",
										}}
										ref={ref => addColorPickerBlockRef(ref, trace.id)}
									/>
								</MobergRow>

								<MobergRow gap="16px">
									<MobergButton
										tooltip={
											sourceIsReadOnly
												? "Post processing is not available for this time-series source."
												: !environmentVariables.FEATURE_FLAG_ROLLING_AVERAGE
													? "Post processing is not available for this workspace."
													: trace.onDemandAnalysis
														? "Edit the calculation of " + trace.onDemandAnalysis.analytic
														: "This modality is a raw signal."
										}
										shape={MobergButtonShape.SQUARE}
										disabled={!environmentVariables.FEATURE_FLAG_ROLLING_AVERAGE || sourceIsReadOnly}
										onClick={() => {
											createModal(
												<EditAnalysisModal
													analysis={trace.onDemandAnalysis}
													rawDataKey={trace.rawDataKey}
													onChange={(newArgs: OnDemandAnalysis | undefined) => traceAnalysisArgumentsChanged(trace, newArgs)}
												/>,
											)
										}}
									>
										<MdSettings size={MobergIconSize.REGULAR} />
									</MobergButton>

									<span style={{ color: "var(--color-text-primary)" }}>
										{trace.onDemandAnalysis ? `(${getAnalyticDisplayName(trace.onDemandAnalysis?.analytic)})` : "(none)"}
									</span>
								</MobergRow>

								<MobergCheckbox isChecked={trace.isVisible ?? true} onChange={isVisible => updateTrace(trace.id, { ...trace, isVisible })} />

								<MobergButton shape={MobergButtonShape.SQUARE} onClick={() => removeTrace(trace.id)} style={{ padding: "6px" }}>
									<MdClose size={MobergIconSize.REGULAR} />
								</MobergButton>
							</React.Fragment>
						)
					})}

					<MobergButton
						onClick={addModality}
						theme={MobergTheme.BLUE}
						variant={MobergButtonVariant.OUTLINE}
						style={{ width: "225px" }}
						disabled={!!reasonAddMeasurementIsDisabled || lineTraceOptions.length === 0}
						tooltip={reasonAddMeasurementIsDisabled}
					>
						Add source data
					</MobergButton>
				</div>
			</MobergColumn>

			<div id={`ColorPickerWindow`} ref={colorPickerRef} style={{ position: "fixed", display: "none", zIndex: 999 }}>
				<TraceColorPicker
					selectedColor={((currentGraph?.traces ?? []).find(trace => trace.id === colorPickerTraceId) as LineTraceConfigJSON)?.color ?? "#000"}
					onChange={color => {
						if (colorPickerTraceId !== undefined) {
							handleColorChange(colorPickerTraceId, color)
						}
					}}
					onClose={() => {
						if (colorPickerRef.current) colorPickerRef.current.style.display = "none"
					}}
				/>
			</div>
		</ModalityConfigurationDiv>
	)
}
